'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { MapPin, Star, Users, Heart, Share2, CheckCircle, Clock, Building2 } from 'lucide-react';

export default function VenueInfoHeader() {
  const [wishlisted, setWishlisted] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6 md:p-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-5 flex-wrap">
        <Link href="/home-page" className="hover:text-red-500 transition-colors">Home</Link>
        <span>/</span>
        <Link href="/venue-listing-page" className="hover:text-red-500 transition-colors">Venues</Link>
        <span>/</span>
        <span className="text-gray-700 font-medium">Mumbai</span>
        <span>/</span>
        <span className="text-gray-700 font-medium truncate">The Grand Meridian</span>
      </div>
      {/* Title Row */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="bg-red-50 text-red-600 text-xs font-bold px-3 py-1 rounded-full border border-red-100">Wedding</span>
            <span className="bg-green-50 text-green-700 text-xs font-bold px-3 py-1 rounded-full border border-green-100 flex items-center gap-1">
              <CheckCircle size={11} />
              Verified Venue
            </span>
            <span className="badge-available">Available</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight">
            The Grand Meridian
          </h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setWishlisted(!wishlisted)}
            className={`w-10 h-10 rounded-xl border-2 flex items-center justify-center transition-all active:scale-90 ${
              wishlisted ? 'border-red-500 bg-red-50 text-red-500' : 'border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-500'
            }`}
            aria-label="Add to wishlist"
          >
            <Heart size={18} className={wishlisted ? 'fill-red-500' : ''} />
          </button>
          <button
            className="w-10 h-10 rounded-xl border-2 border-gray-200 flex items-center justify-center text-gray-500 hover:border-gray-300 transition-colors"
            aria-label="Share venue"
          >
            <Share2 size={18} />
          </button>
        </div>
      </div>
      {/* Location */}
      <div className="flex items-center gap-1.5 text-gray-600 mb-5">
        <MapPin size={15} className="text-red-500 shrink-0" />
        <span className="text-sm font-medium">Bandra West, Mumbai, Maharashtra 400050</span>
      </div>
      {/* Rating + Stats */}
      <div className="flex flex-wrap gap-6 mb-6 p-4 bg-gray-50 rounded-xl">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            {Array.from({ length: 5 })?.map((_, i) => (
              <Star key={`detail-star-${i}`} size={16} className={i < 5 ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'} />
            ))}
          </div>
          <span className="font-bold text-gray-900 text-lg font-tabular">4.9</span>
          <span className="text-gray-500 text-sm">(312 reviews)</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          <Users size={15} className="text-red-400" />
          <span className="text-sm font-semibold">Up to <span className="text-gray-900">800 guests</span></span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          <Building2 size={15} className="text-red-400" />
          <span className="text-sm font-semibold"><span className="text-gray-900">Indoor</span> venue</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          <Clock size={15} className="text-red-400" />
          <span className="text-sm font-semibold">Available <span className="text-gray-900">8 AM – 12 AM</span></span>
        </div>
      </div>
      {/* Description */}
      <div>
        <h2 className="font-bold text-gray-900 text-base mb-2">About This Venue</h2>
        <p className="text-gray-600 text-sm leading-relaxed">
          The Grand Meridian is one of Mumbai&apos;s most sought-after luxury wedding and event venues, located in the heart of Bandra West. Spanning 18,000 sq ft across two grand halls, it can accommodate intimate gatherings of 100 to grand celebrations of 800 guests. The venue features state-of-the-art AV systems, in-house catering by award-winning chefs, valet parking for 200 vehicles, and a dedicated event coordination team. Whether it&apos;s a royal wedding, corporate gala, or milestone anniversary, The Grand Meridian delivers an unforgettable experience every time.
        </p>
      </div>
      {/* Pricing Summary */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="border-2 border-gray-100 rounded-xl p-4 hover:border-red-200 transition-colors">
          <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">Hourly Booking</div>
          <div className="text-2xl font-extrabold text-red-600 font-tabular">₹15,000</div>
          <div className="text-xs text-gray-400 mt-0.5">Per hour · Min 4 hours</div>
        </div>
        <div className="border-2 border-red-100 bg-red-50 rounded-xl p-4">
          <div className="text-xs text-red-600 font-semibold uppercase tracking-wider mb-1">Full Day · Best Value</div>
          <div className="text-2xl font-extrabold text-red-700 font-tabular">₹95,000</div>
          <div className="text-xs text-red-400 mt-0.5">8 AM – 12 AM · Save ₹25,000</div>
        </div>
      </div>
    </div>
  );
}